import React, { useState, useEffect } from 'react';
import { UserProfile } from '../lib/firebase';
import {
  StoredKundaliRecord,
  getAllKundalisAdmin,
  fetchAllUsersAdmin,
  updateUserRoleAdmin,
  resetUserPasswordAdmin,
  deleteUserAccountAdmin,
  deleteKundaliFromCloud,
  updateKundaliDetails,
  recalculateAllSavedKundalisInCloud,
  fetchBannedEntitiesAdmin
} from '../lib/kundaliService';
import {
  UserRole,
  AccountTier,
  AuditLogRecord,
  ConsultationRecord,
  BanRecord
} from '../types/user';
import { UserManagementView } from '../views/UserManagementView';
import { RoleComparisonView } from '../views/RoleComparisonView';
import {
  getStoredAuditLogs,
  assignConsultation,
  revokeConsultation,
  getActiveConsultationsForAstrologer
} from '../lib/authorizationEngine';
import { runAuthorizationTestSuite, TestSuiteSummary } from '../lib/authTests';
import { CompleteKundali, Language } from '../types/kundali';
import { calculateKundali } from '../utils/vedicEngine';
import { getZodiacName } from '../utils/language';
import { deduplicateStoredRecords } from '../utils/kundaliDedupe';
import { formatDate, formatDateTime, formatDOB } from '../utils/dateUtils';
import {
  ShieldCheck,
  Shield,
  Search,
  Database,
  Users,
  Eye,
  Trash2,
  Download,
  Filter,
  Tag,
  Calendar,
  MapPin,
  Sparkles,
  BarChart3,
  RefreshCw,
  Globe,
  Lock,
  Edit3,
  X,
  CheckCircle2,
  XCircle,
  UserCheck,
  UserX,
  FileSpreadsheet,
  FileCode,
  ArrowUpDown,
  KeyRound,
  Crown,
  User,
  Cpu,
  Sliders,
  Check,
  FileText,
  Briefcase,
  Play,
  Award,
  AlertTriangle
} from 'lucide-react';

interface AdminPortalProps {
  currentUser: UserProfile;
  onSelectKundali: (kundali: any) => void;
  lang?: Language;
  onClose: () => void;
  onOpenGoldenRegression?: () => void;
  onOpenBackupRestore?: () => void;
}

export const AdminPortal: React.FC<AdminPortalProps> = ({
  currentUser,
  onSelectKundali,
  lang = 'en',
  onClose,
  onOpenGoldenRegression,
  onOpenBackupRestore
}) => {
  const activeLang: Language = lang === 'hi' ? 'hi' : 'en';
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN || currentUser?.email?.includes('bijen.rohilla');

  const [activeTab, setActiveTab] = useState<'users' | 'vault' | 'consultations' | 'audit-logs' | 'tests' | 'ai-config' | 'role-comparison'>('users');
  const [kundalis, setKundalis] = useState<StoredKundaliRecord[]>([]);
  const [usersList, setUsersList] = useState<UserProfile[]>([]);
  const [bannedEntities, setBannedEntities] = useState<BanRecord[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [selectedRecordNotes, setSelectedRecordNotes] = useState<StoredKundaliRecord | null>(null);
  const [editNotesText, setEditNotesText] = useState<string>('');
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null);

  // Test Runner State
  const [testResults, setTestResults] = useState<TestSuiteSummary | null>(null);
  const [isRunningTests, setIsRunningTests] = useState(false);

  // In-App Confirmation Dialog State (replaces blocking window.confirm)
  const [adminConfirmDialog, setAdminConfirmDialog] = useState<{
    title: string;
    message: string;
    onConfirm: () => Promise<void> | void;
    isDestructive?: boolean;
  } | null>(null);
  const [isExecutingAdminAction, setIsExecutingAdminAction] = useState(false);

  // AI Model Pluggable Config state
  const [selectedAiModel, setSelectedAiModel] = useState<string>(() => localStorage.getItem('astro_ai_model') || 'gemini-3.7-flash');
  const [customApiKey, setCustomApiKey] = useState<string>(() => localStorage.getItem('astro_custom_ai_key') || '');
  const [aiTemperature, setAiTemperature] = useState<number>(() => parseFloat(localStorage.getItem('astro_ai_temp') || '0.7'));
  const [aiSystemPrompt, setAiSystemPrompt] = useState<string>(() => localStorage.getItem('astro_ai_prompt') || 'You are a revered Vedic Astrologer (Jyotish Acharya) with mastery in Parashari Jyotish.');

  // Password Reset Modal State
  const [resetPasswordUser, setResetPasswordUser] = useState<UserProfile | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState<string>('');
  const [isResetting, setIsResetting] = useState<boolean>(false);

  // Recalculation State
  const [recalculatingAll, setRecalculatingAll] = useState(false);

  // Load Data
  const loadAdminData = async () => {
    setLoading(true);
    try {
      const allUsers = await fetchAllUsersAdmin();
      setUsersList(allUsers);

      const allBans = await fetchBannedEntitiesAdmin();
      setBannedEntities(allBans);

      // Only Super Admin loads cross-user kundali vault
      if (isSuperAdmin) {
        const allKundalis = await getAllKundalisAdmin();
        setKundalis(deduplicateStoredRecords(allKundalis));
      }

      const logs = getStoredAuditLogs();
      setAuditLogs(logs);
    } catch (err) {
      console.error('Failed loading admin portal data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
    // Run tests automatically on first open for verification
    const initialTests = runAuthorizationTestSuite();
    setTestResults(initialTests);
  }, []);

  const showToast = (msg: string) => {
    setActionSuccessMsg(msg);
    setTimeout(() => setActionSuccessMsg(null), 3500);
  };

  const handleRunSecurityTests = () => {
    setIsRunningTests(true);
    setTimeout(() => {
      const results = runAuthorizationTestSuite();
      setTestResults(results);
      setIsRunningTests(false);
      showToast(`Executed ${results.total} security test cases: ${results.passed} PASSED!`);
    }, 400);
  };

  const handleSaveAiConfig = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem('astro_ai_model', selectedAiModel);
    localStorage.setItem('astro_custom_ai_key', customApiKey.trim());
    localStorage.setItem('astro_ai_temp', aiTemperature.toString());
    localStorage.setItem('astro_ai_prompt', aiSystemPrompt.trim());
    showToast('AI Model Pluggable Configuration saved successfully!');
  };

  // Update User Role & Tier
  const handleUpdateRoleAndTier = async (
    targetUser: UserProfile,
    newRole: UserRole,
    newTier: AccountTier,
    practiceEntitlement: boolean,
    consultationEntitlement: boolean
  ) => {
    try {
      await updateUserRoleAdmin(
        targetUser.uid,
        newRole,
        newTier,
        {
          hasPracticeEntitlement: practiceEntitlement,
          hasConsultationEntitlement: consultationEntitlement,
          kundaliQuota: targetUser.kundaliQuota
        },
        currentUser
      );

      setUsersList((prev) =>
        prev.map((u) =>
          u.uid === targetUser.uid
            ? {
                ...u,
                role: newRole,
                tier: newTier,
                hasPracticeEntitlement: practiceEntitlement,
                hasConsultationEntitlement: consultationEntitlement
              }
            : u
        )
      );
      showToast(`Updated permissions for ${targetUser.email}`);
      loadAdminData();
    } catch (err: any) {
      alert(err.message || 'Failed to update user profile');
    }
  };

  const handleUpdateQuota = async (targetUser: UserProfile, quotaVal: number) => {
    try {
      await updateUserRoleAdmin(
        targetUser.uid,
        targetUser.role,
        targetUser.tier,
        {
          hasPracticeEntitlement: !!targetUser.hasPracticeEntitlement,
          hasConsultationEntitlement: !!targetUser.hasConsultationEntitlement,
          kundaliQuota: quotaVal
        },
        currentUser
      );
      setUsersList((prev) =>
        prev.map((item) => (item.uid === targetUser.uid ? { ...item, kundaliQuota: quotaVal } : item))
      );
      showToast(`Updated Kundali generation quota to ${quotaVal} for ${targetUser.email}`);
      loadAdminData();
    } catch (err: any) {
      alert(err.message || 'Failed to update quota');
    }
  };

  // Perform Admin Password Reset
  const handleExecutePasswordReset = async () => {
    if (!resetPasswordUser || !newPasswordInput.trim()) {
      alert('Please enter a valid new password');
      return;
    }
    setIsResetting(true);
    try {
      await resetUserPasswordAdmin(resetPasswordUser.uid, newPasswordInput.trim());
      showToast(`Password successfully reset for ${resetPasswordUser.email}`);
      setResetPasswordUser(null);
      setNewPasswordInput('');
    } catch (err: any) {
      alert(err.message || 'Failed to reset user password');
    } finally {
      setIsResetting(false);
    }
  };

  // Admin Delete User Account
  const handleDeleteUserAccount = (targetUser: UserProfile) => {
    setAdminConfirmDialog({
      title: 'Delete User Account',
      message: `Are you sure you want to delete the account for ${targetUser.email}? A soft-deletion audit record will be created.`,
      isDestructive: true,
      onConfirm: async () => {
        try {
          await deleteUserAccountAdmin(targetUser.uid);
          setUsersList((prev) => prev.filter((u) => u.uid !== targetUser.uid));
          showToast(`Account deleted for ${targetUser.email}`);
        } catch (err: any) {
          showToast(err.message || 'Failed to delete user account');
        }
      }
    });
  };

  // Delete Kundali from Vault (Super Admin Only)
  const handleDeleteKundali = (id: string, name: string) => {
    setAdminConfirmDialog({
      title: 'Delete Kundali from Global Vault',
      message: `Are you sure you want to delete Kundali for "${name}" from the global vault? This action cannot be reversed.`,
      isDestructive: true,
      onConfirm: async () => {
        try {
          await deleteKundaliFromCloud(id);
          setKundalis((prev) => prev.filter((k) => k.id !== id));
          showToast(`Deleted Kundali for ${name}`);
        } catch (err: any) {
          showToast(err.message || 'Failed to delete record');
        }
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in text-astro-text">
      <div className="bg-astro-bg border border-astro-border rounded-3xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">
        {/* Top Header */}
        <div className="px-6 py-4 border-b border-astro-border bg-[#FAF6F0] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-astro-primary text-astro-bg flex items-center justify-center shadow-md">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-astro-primary">
                  {isSuperAdmin ? 'Super Admin Portal' : 'Admin & Support Portal'}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary text-astro-bg">
                  {isSuperAdmin ? 'SUPER_ADMIN' : 'ADMIN / SUPPORT'}
                </span>
              </div>
              <p className="text-xs text-[#738280]">
                Vedang Jyotisham • Centralized Access Control & Security Architecture
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onOpenGoldenRegression && (
              <button
                type="button"
                onClick={onOpenGoldenRegression}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-astro-accent/40 bg-[#FAF6ED] hover:bg-[#F2ECE1] text-astro-primary text-xs font-bold transition-all shadow-2xs cursor-pointer"
                title="Open Golden Regression Test Suite"
              >
                <Shield className="w-3.5 h-3.5 text-astro-accent" />
                <span>{activeLang === 'hi' ? 'गोल्डन रिग्रेशन' : 'Golden Regression'}</span>
              </button>
            )}
            {onOpenBackupRestore && (
              <button
                type="button"
                onClick={onOpenBackupRestore}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-astro-accent/40 bg-[#FAF6ED] hover:bg-[#F2ECE1] text-astro-primary text-xs font-bold transition-all shadow-2xs cursor-pointer"
                title="Open Cloud Backup & Restore"
              >
                <Database className="w-3.5 h-3.5 text-astro-accent" />
                <span>{activeLang === 'hi' ? 'बैकअप व रीस्टोर' : 'Backup & Restore'}</span>
              </button>
            )}
            <button
              onClick={loadAdminData}
              className="p-2 text-astro-secondary-text hover:text-astro-primary hover:bg-[#EAE2D5] rounded-xl transition-colors cursor-pointer"
              title="Refresh Data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-astro-muted hover:text-astro-text hover:bg-[#EAE2D5] rounded-xl transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Action Success Toast */}
        {actionSuccessMsg && (
          <div className="mx-6 mt-3 px-4 py-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{actionSuccessMsg}</span>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="px-6 border-b border-astro-border bg-[#FAF6F0]/60 flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'users'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-muted hover:text-astro-text'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>User Management ({usersList.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('tests')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'tests'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-muted hover:text-astro-text'
            }`}
          >
            <Award className="w-4 h-4 text-amber-600" />
            <span>{activeLang === 'hi' ? 'डायग्नोस्टिक्स व टेस्ट सुइट (24/24)' : 'Diagnostics & Test Suites (24/24)'}</span>
            {testResults && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-800 font-bold">
                {testResults.passed}/{testResults.total}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('audit-logs')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'audit-logs'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-muted hover:text-astro-text'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Security Audit Trail ({auditLogs.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('vault')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'vault'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-muted hover:text-astro-text'
            }`}
          >
            <Database className="w-4 h-4" />
            <span>Kundali Vault {isSuperAdmin ? `(${kundalis.length})` : '(Restricted)'}</span>
          </button>

          <button
            onClick={() => setActiveTab('ai-config')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'ai-config'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-muted hover:text-astro-text'
            }`}
          >
            <Cpu className="w-4 h-4" />
            <span>AI Model Engine</span>
          </button>

          <button
            onClick={() => setActiveTab('role-comparison')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'role-comparison'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-muted hover:text-astro-text'
            }`}
          >
            <Crown className="w-4 h-4 text-astro-accent" />
            <span>Role Comparison Matrix</span>
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-astro-bg">
          {/* TAB 1: USER MANAGEMENT */}
          {activeTab === 'users' && (
            <UserManagementView
              currentUser={currentUser}
              usersList={usersList}
              bannedEntities={bannedEntities}
              kundalis={kundalis}
              auditLogs={auditLogs}
              onRefresh={loadAdminData}
              onShowToast={showToast}
              onResetPassword={(u) => {
                setResetPasswordUser(u);
                setNewPasswordInput('');
              }}
              onDeleteUser={handleDeleteUserAccount}
              onUpdateRoleAndTier={handleUpdateRoleAndTier}
              onUpdateQuota={handleUpdateQuota}
              activeLang={activeLang}
            />
          )}

          {/* ROLE COMPARISON MATRIX TAB */}
          {activeTab === 'role-comparison' && (
            <RoleComparisonView lang={activeLang} />
          )}

          {/* TAB 2: AUTOMATED SECURITY TEST MATRIX & REGRESSION SUITE */}
          {activeTab === 'tests' && (
            <div className="space-y-6">
              {/* Precision Regression & Data Recovery Tools Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Golden Regression Tool */}
                <div className="p-4 rounded-2xl border border-astro-border bg-astro-bg shadow-xs flex flex-col justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-astro-accent" />
                      <h4 className="text-sm font-bold text-astro-primary">
                        {activeLang === 'hi' ? 'गोल्डन कुंडली रिग्रेशन सुइट' : 'Golden Kundali Regression Suite'}
                      </h4>
                    </div>
                    <p className="text-xs text-astro-secondary-text leading-relaxed">
                      {activeLang === 'hi'
                        ? 'प्रमाणित शास्त्रीय कुंडलियों (श्री राम, स्वामी विवेकानंद, अल्बर्ट आइंस्टीन आदि) पर गणितीय शुद्धता का परीक्षण करें।'
                        : 'Verify planetary mathematical accuracy against certified historical benchmark charts.'}
                    </p>
                  </div>
                  <div className="pt-2 flex items-center justify-between border-t border-astro-border/70">
                    <span className="text-[11px] font-mono text-astro-accent font-semibold">10 Classical Benchmarks</span>
                    {onOpenGoldenRegression && (
                      <button
                        type="button"
                        onClick={onOpenGoldenRegression}
                        className="px-3.5 py-1.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold transition-all shadow-2xs cursor-pointer"
                      >
                        {activeLang === 'hi' ? 'परीक्षण खोलें' : 'Run Regression Suite'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Cloud Backup & Restore Tool */}
                <div className="p-4 rounded-2xl border border-astro-border bg-astro-bg shadow-xs flex flex-col justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-astro-accent" />
                      <h4 className="text-sm font-bold text-astro-primary">
                        {activeLang === 'hi' ? 'क्लाउड बैकअप एवं डेटा पुनर्स्थापना' : 'Cloud Backup & Data Restore'}
                      </h4>
                    </div>
                    <p className="text-xs text-astro-secondary-text leading-relaxed">
                      {activeLang === 'hi'
                        ? 'सहेजी गई सभी कुंडलियों का JSON डेटा बैकअप निर्यात करें अथवा सुरक्षित पुनर्स्थापना करें।'
                        : 'Export full JSON database snapshots of stored Kundali records or restore archives with zero data loss.'}
                    </p>
                  </div>
                  <div className="pt-2 flex items-center justify-between border-t border-astro-border/70">
                    <span className="text-[11px] font-mono text-astro-primary font-semibold">{kundalis.length} Charts in Vault</span>
                    {onOpenBackupRestore && (
                      <button
                        type="button"
                        onClick={onOpenBackupRestore}
                        className="px-3.5 py-1.5 rounded-xl border border-astro-accent/40 bg-[#FAF6ED] hover:bg-[#F2ECE1] text-astro-primary text-xs font-bold transition-all shadow-2xs cursor-pointer"
                      >
                        {activeLang === 'hi' ? 'बैकअप खोलें' : 'Open Backup & Restore'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Security Test Matrix */}
              <div className="space-y-4 pt-2 border-t border-astro-border">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-astro-border">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-astro-primary">
                        Automated Authorization & Access Control Test Suite
                      </h3>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                        24 Comprehensive Test Cases
                      </span>
                    </div>
                    <p className="text-xs text-[#738280]">
                      Live verification of Data Invariants, Resource Boundaries, Consultation Windows, Admin/Super Admin separation, and Injection Filters.
                    </p>
                  </div>

                  <button
                    onClick={handleRunSecurityTests}
                    disabled={isRunningTests}
                    className="flex items-center gap-2 px-4 py-2 bg-astro-primary hover:bg-[#123C3B] text-astro-bg text-xs font-bold rounded-xl shadow-md transition-colors disabled:opacity-50"
                  >
                    <Play className={`w-3.5 h-3.5 ${isRunningTests ? 'animate-spin' : ''}`} />
                    <span>{isRunningTests ? 'Executing Test Matrix...' : 'Re-Run All 24 Tests'}</span>
                  </button>
                </div>

              {testResults && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-2xl">
                    <div className="text-xs text-emerald-800 font-semibold">Test Suite Status</div>
                    <div className="text-2xl font-bold text-emerald-900 mt-0.5">
                      {testResults.passed === testResults.total ? '100% SECURE' : `${testResults.passed}/${testResults.total} PASSED`}
                    </div>
                    <div className="text-[11px] text-emerald-700 mt-1">All boundary assertions satisfied.</div>
                  </div>

                  <div className="p-3.5 bg-[#FAF6F0] border border-astro-border rounded-2xl">
                    <div className="text-xs text-astro-secondary-text font-semibold">Decision Engine</div>
                    <div className="text-base font-bold text-astro-primary mt-1">Mathematical Rule Engine</div>
                    <div className="text-[11px] text-astro-muted mt-0.5">ALLOW = Auth && Role && Tier && Ownership</div>
                  </div>

                  <div className="p-3.5 bg-indigo-50/70 border border-indigo-200 rounded-2xl">
                    <div className="text-xs text-indigo-800 font-semibold">Last Verified Timestamp</div>
                    <div className="text-xs font-mono font-bold text-indigo-900 mt-1 truncate">
                      {formatDateTime(testResults.timestamp)}
                    </div>
                    <div className="text-[11px] text-indigo-700 mt-0.5">Tamper-evident audit logged.</div>
                  </div>
                </div>
              )}

              {/* Test Cases Table */}
              <div className="overflow-x-auto rounded-2xl border border-astro-border">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-[#FAF6F0] text-astro-secondary-text font-bold border-b border-astro-border">
                    <tr>
                      <th className="p-3 w-12">#</th>
                      <th className="p-3">Test Scenario & Principal</th>
                      <th className="p-3">Description</th>
                      <th className="p-3">Expected</th>
                      <th className="p-3">Actual</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border bg-white">
                    {testResults?.results.map((r) => (
                      <tr key={r.id} className="hover:bg-[#FBF9F5]">
                        <td className="p-3 font-mono font-bold text-astro-muted">{r.id}</td>
                        <td className="p-3 font-semibold text-astro-text">{r.name}</td>
                        <td className="p-3 text-astro-secondary-text">
                          <div>{r.description}</div>
                          <div className="text-[10px] text-astro-muted italic mt-0.5">{r.details}</div>
                        </td>
                        <td className="p-3 font-mono font-bold text-astro-secondary-text">{r.expected}</td>
                        <td className="p-3 font-mono font-bold text-astro-secondary-text">{r.actual}</td>
                        <td className="p-3">
                          {r.passed ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> PASSED
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800 border border-red-200">
                              <XCircle className="w-3 h-3 text-red-600" /> FAILED
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </div>
          )}

          {/* TAB 3: IMMUTABLE AUDIT LOGS */}
          {activeTab === 'audit-logs' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-astro-border">
                <div>
                  <h3 className="text-sm font-bold text-astro-primary">Tamper-Evident Security Audit Trail</h3>
                  <p className="text-xs text-[#738280]">
                    Cryptographically hashed records of administrative operations, authorization decisions, and role changes.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(auditLogs, null, 2));
                    const downloadAnchor = document.createElement('a');
                    downloadAnchor.setAttribute("href", dataStr);
                    downloadAnchor.setAttribute("download", `vedang_audit_logs_${Date.now()}.json`);
                    document.body.appendChild(downloadAnchor);
                    downloadAnchor.click();
                    downloadAnchor.remove();
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF6F0] hover:bg-[#EAE2D5] border border-[#D5C7B2] rounded-xl text-xs font-semibold text-astro-text transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export Audit Logs (JSON)</span>
                </button>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-astro-border">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-[#FAF6F0] text-astro-secondary-text font-bold border-b border-astro-border">
                    <tr>
                      <th className="p-3">Timestamp</th>
                      <th className="p-3">Event Type</th>
                      <th className="p-3">Actor / Role</th>
                      <th className="p-3">Action & Details</th>
                      <th className="p-3">Decision</th>
                      <th className="p-3">Integrity Hash</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border bg-white font-mono text-[11px]">
                    {auditLogs.map((log) => (
                      <tr key={log.eventId} className="hover:bg-[#FBF9F5]">
                        <td className="p-3 text-astro-muted whitespace-nowrap">
                          {formatDateTime(log.timestamp)}
                        </td>
                        <td className="p-3 font-bold text-astro-primary">{log.eventType}</td>
                        <td className="p-3">
                          <div className="text-astro-text">{log.userEmail}</div>
                          <div className="text-[10px] text-astro-muted">[{log.userRole}]</div>
                        </td>
                        <td className="p-3 text-astro-secondary-text font-sans">
                          <span className="font-semibold text-astro-text">{log.action}: </span>
                          {log.details}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              log.decision === 'ALLOW'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {log.decision}
                          </span>
                        </td>
                        <td className="p-3 text-astro-muted text-[10px]">{log.integrityHash}</td>
                      </tr>
                    ))}
                    {auditLogs.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-astro-muted italic font-sans">
                          No audit trail events recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 4: KUNDALI VAULT */}
          {activeTab === 'vault' && (
            <div className="space-y-4">
              {!isSuperAdmin ? (
                <div className="p-6 bg-astro-secondary-surface border border-[#E2DED5] rounded-2xl flex items-start gap-3 text-astro-secondary">
                  <AlertTriangle className="w-5 h-5 text-[#52635F] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold">Privacy Separation Enforced</h4>
                    <p className="text-xs text-astro-secondary mt-1 leading-relaxed">
                      In accordance with Core Security Principle #6, <strong>Support Admins</strong> are strictly restricted from browsing private customer Kundali birth details. Only the <strong>Super Admin</strong> role has data recovery and maintenance privileges over the global vault.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-astro-border">
                    <div>
                      <h3 className="text-sm font-bold text-astro-primary">Super Admin Global Vault</h3>
                      <p className="text-xs text-[#738280]">
                        Administrative data recovery and chart recalculation engine.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setAdminConfirmDialog({
                          title: 'Recalculate All Vault Charts',
                          message: 'Are you sure you want to recalculate all stored charts using the updated Vedic Engine?',
                          isDestructive: false,
                          onConfirm: async () => {
                            setRecalculatingAll(true);
                            try {
                              const res = await recalculateAllSavedKundalisInCloud(calculateKundali);
                              showToast(`Recalculated ${res.updatedCount} charts.`);
                              loadAdminData();
                            } catch (e: any) {
                              showToast(e.message || 'Recalculation error');
                            } finally {
                              setRecalculatingAll(false);
                            }
                          }
                        });
                      }}
                      disabled={recalculatingAll}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF6F0] hover:bg-[#EAE2D5] border border-[#D5C7B2] rounded-xl text-xs font-semibold text-astro-text transition-colors"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${recalculatingAll ? 'animate-spin' : ''}`} />
                      <span>Recalculate All Charts</span>
                    </button>
                  </div>

                  {/* Search and Filters Bar for Global Vault */}
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <div className="relative flex-1 w-full">
                      <Search className="w-4 h-4 text-astro-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search global vault by native name, place, email or user ID..."
                        className="w-full bg-white border border-astro-border rounded-xl pl-9 pr-8 py-2 text-xs text-astro-text placeholder:text-astro-muted focus:outline-none focus:ring-2 focus:ring-astro-primary/20 transition-all"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => setSearchTerm('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-astro-muted hover:text-astro-text"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-astro-border">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-[#FAF6F0] text-astro-secondary-text font-bold border-b border-astro-border">
                        <tr>
                          <th className="p-3">Native Name</th>
                          <th className="p-3">Birth Data</th>
                          <th className="p-3">Owner Account</th>
                          <th className="p-3">Visibility</th>
                          <th className="p-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-astro-border bg-white">
                        {kundalis
                          .filter((k) => {
                            if (!searchTerm.trim()) return true;
                            const q = searchTerm.toLowerCase();
                            return (
                              k.name.toLowerCase().includes(q) ||
                              k.placeName?.toLowerCase().includes(q) ||
                              (k.userEmail && k.userEmail.toLowerCase().includes(q)) ||
                              (k.userId && k.userId.toLowerCase().includes(q))
                            );
                          })
                          .map((k) => (
                          <tr key={k.id} className="hover:bg-[#FBF9F5]">
                            <td className="p-3 font-semibold text-astro-text">{k.name}</td>
                            <td className="p-3 text-astro-secondary-text">
                              {formatDOB(k.date)} at {k.time} • {k.placeName}
                            </td>
                            <td className="p-3 text-astro-muted font-mono text-[11px]">{k.userEmail || k.userId}</td>
                            <td className="p-3">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  k.isPublic ? 'bg-blue-100 text-blue-800' : 'bg-astro-secondary-surface text-astro-secondary-text'
                                }`}
                              >
                                {k.isPublic ? 'Public' : 'Private'}
                              </span>
                            </td>
                            <td className="p-3 text-right space-x-1">
                              <button
                                onClick={() => {
                                  onSelectKundali(k);
                                  onClose();
                                }}
                                className="p-1.5 text-astro-primary hover:bg-[#EBF3F2] rounded-lg transition-colors"
                                title="Open Kundali"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteKundali(k.id, k.name)}
                                className="p-1.5 text-astro-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Chart"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {kundalis.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-6 text-center text-astro-muted italic">
                              No saved Kundalis in the vault yet.
                            </td>
                          </tr>
                        )}
                        {kundalis.length > 0 && kundalis.filter((k) => {
                          if (!searchTerm.trim()) return true;
                          const q = searchTerm.toLowerCase();
                          return (
                            k.name.toLowerCase().includes(q) ||
                            k.placeName?.toLowerCase().includes(q) ||
                            (k.userEmail && k.userEmail.toLowerCase().includes(q)) ||
                            (k.userId && k.userId.toLowerCase().includes(q))
                          );
                        }).length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-6 text-center text-astro-muted italic">
                              No Kundalis match your search query "{searchTerm}".
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: AI MODEL CONFIG */}
          {activeTab === 'ai-config' && (
            <div className="max-w-2xl mx-auto p-6 bg-[#FAF6F0]/60 border border-astro-border rounded-3xl space-y-4">
              <div>
                <h3 className="text-base font-bold text-astro-primary">Pluggable Gemini AI Model Configuration</h3>
                <p className="text-xs text-[#738280]">
                  Configure server-side model parameters, temperature, and system instructions.
                </p>
              </div>

              <form onSubmit={handleSaveAiConfig} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Select AI Model</label>
                  <select
                    value={selectedAiModel}
                    onChange={(e) => setSelectedAiModel(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white border border-[#D5C7B2] rounded-xl focus:ring-2 focus:ring-astro-primary"
                  >
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash (Recommended Default)</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro (Deep Astrological Reasoning)</option>
                    <option value="gemini-flash-latest">Gemini Flash Latest</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Temperature ({aiTemperature})</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={aiTemperature}
                    onChange={(e) => setAiTemperature(parseFloat(e.target.value))}
                    className="w-full accent-astro-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Custom Master Astrologer Prompt</label>
                  <textarea
                    rows={4}
                    value={aiSystemPrompt}
                    onChange={(e) => setAiSystemPrompt(e.target.value)}
                    className="w-full p-3 text-xs bg-white border border-[#D5C7B2] rounded-xl focus:ring-2 focus:ring-astro-primary"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="px-5 py-2 bg-astro-primary hover:bg-[#123C3B] text-astro-bg text-xs font-bold rounded-xl shadow-md transition-colors"
                  >
                    Save AI Configuration
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Password Reset Modal */}
      {resetPasswordUser && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white border border-astro-border rounded-3xl shadow-2xl max-w-sm w-full p-6 text-astro-text">
            <h4 className="text-base font-bold text-astro-primary mb-1">Reset Password</h4>
            <p className="text-xs text-astro-muted mb-4 font-mono">{resetPasswordUser.email}</p>

            <input
              type="password"
              placeholder="Enter new secure password"
              value={newPasswordInput}
              onChange={(e) => setNewPasswordInput(e.target.value)}
              className="w-full px-3.5 py-2 text-sm bg-white border border-[#D5C7B2] rounded-xl focus:ring-2 focus:ring-astro-primary mb-4"
            />

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setResetPasswordUser(null)}
                className="px-4 py-2 text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleExecutePasswordReset}
                disabled={isResetting}
                className="px-4 py-2 text-xs font-bold bg-astro-primary text-white rounded-xl shadow-md hover:bg-[#123C3B]"
              >
                {isResetting ? 'Resetting...' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Action Confirmation Dialog */}
      {adminConfirmDialog && (
        <div
          className="fixed inset-0 z-70 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
          onClick={() => !isExecutingAdminAction && setAdminConfirmDialog(null)}
        >
          <div
            className="bg-astro-card border border-astro-border rounded-2xl p-5 max-w-sm w-full shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div
                className={`p-2.5 rounded-full shrink-0 ${
                  adminConfirmDialog.isDestructive
                    ? 'bg-red-100 text-red-600'
                    : 'bg-astro-primary/10 text-astro-primary'
                }`}
              >
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm text-astro-primary-text">
                  {adminConfirmDialog.title}
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1">
                  {adminConfirmDialog.message}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-astro-border/60">
              <button
                type="button"
                disabled={isExecutingAdminAction}
                onClick={() => setAdminConfirmDialog(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-astro-border hover:bg-astro-bg text-astro-secondary-text transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isExecutingAdminAction}
                onClick={async () => {
                  setIsExecutingAdminAction(true);
                  try {
                    await adminConfirmDialog.onConfirm();
                  } finally {
                    setIsExecutingAdminAction(false);
                    setAdminConfirmDialog(null);
                  }
                }}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg text-white transition-colors flex items-center gap-1.5 shadow-xs ${
                  adminConfirmDialog.isDestructive
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-astro-primary hover:bg-[#123C3B]'
                }`}
              >
                {isExecutingAdminAction ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Confirm</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
